import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  ActionButtonPair,
  BalanceCard,
  Card,
  ErrorState,
  MoneyInput,
  PageHeader,
  Skeleton,
  SuccessScreen,
} from '@components/ui';
import { useBusiness } from '@contexts/business/BusinessContext';
import useLayoutNavigation from '@hooks/useLayoutNavigation';
import { accountService } from '@services/api/accountService';
import { WithdrawalAccount } from '@typeDefs/domain/entities/accounts';
import { formatCurrency, formatSortCode } from '@utils/formatters';
import { formatInsufficientFundsMessage, hasFeeGap } from '@/constants/saleErrorMessages';
import styles from './Withdrawal.module.css';

interface WithdrawalProps {
  onBackClick: () => void;
  onCancel: () => void;
  onBackToManageAssets: () => void;
}

const Withdrawal: React.FC<WithdrawalProps> = ({
  onBackClick: _onBackClick,
  onCancel: _onCancel,
  onBackToManageAssets: _onBackToManageAssets,
}) => {
  const { navigateFromLogoOnlyWithFadeOut } = useLayoutNavigation();
  const { businessProfile, refreshBusinessData } = useBusiness();
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [isContentFading, setIsContentFading] = useState<boolean>(false);

  // Ref for the amount input
  const amountInputRef = useRef<HTMLInputElement>(null);

  // Stable per-action idempotency key. Minted once per in-flight submission and
  // reused across any concurrent re-invocation (double-click) so the server
  // dedupes them as the same request. Cleared after the attempt settles.
  const idempotencyKeyRef = useRef<string | null>(null);

  // Withdrawal account state
  const [withdrawalAccount, setWithdrawalAccount] = useState<WithdrawalAccount | null>(null);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(true);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  // Auto-focus input on mount since custom amount is default
  useEffect(() => {
    const timer = setTimeout(() => {
      amountInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Fetch withdrawal accounts on mount
  useEffect(() => {
    const fetchWithdrawalAccounts = async () => {
      if (!businessProfile?.id) {
        setAccountsLoading(false);
        return;
      }

      try {
        setAccountsLoading(true);
        setAccountsError(null);

        const accounts = await accountService.getWithdrawalAccounts(businessProfile.id);

        if (accounts.length === 0) {
          setAccountsError('No withdrawal accounts configured');
          setWithdrawalAccount(null);
        } else {
          // Use the first account (no default flag check)
          setWithdrawalAccount(accounts[0]);
        }
      } catch (error) {
        console.error('Error fetching withdrawal accounts:', error);
        setAccountsError('Failed to load withdrawal accounts');
        setWithdrawalAccount(null);
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchWithdrawalAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const handleBackClick = () => {
    navigateFromLogoOnlyWithFadeOut('/wallet', () => {
      setIsContentFading(true);
    });
  };

  const handleHomeClick = () => {
    navigateFromLogoOnlyWithFadeOut('/', () => {
      setIsContentFading(true);
    });
  };

  // ── #160b (feature preservoir-ai#117) — two balances, deliberately two names ──
  //
  // These MUST stay separate bindings. This page previously had one memo named
  // `availableBalance` for the display plus two inline reads of the same field
  // for the caps; flipping that single memo to gross would have silently raised
  // the withdrawal cap by the accrued-fee amount, letting a client withdraw money
  // they owe in fees.
  //
  //   grossBalance   → DISPLAY ONLY (the balance card). What the ledger holds.
  //   netSpendCap    → EVERY GUARD. What the client may actually withdraw.
  const grossBalance = useMemo(() => {
    return businessProfile?.virtualCashAccount?.grossAvailableBalance || 0;
  }, [businessProfile]);

  const netSpendCap = useMemo(() => {
    return businessProfile?.virtualCashAccount?.availableBalance ?? 0;
  }, [businessProfile]);

  // #161 — the accrued-unpaid fee that accounts for the gap between the two
  // figures above. Display-adjacent only: it explains the cap, it never moves it.
  const feesPayableBalance = useMemo(() => {
    return businessProfile?.virtualCashAccount?.feesPayableBalance ?? 0;
  }, [businessProfile]);

  /**
   * The cap-refusal copy (#161).
   *
   * Since #160b this page shows GROSS and caps at NET, so a client looking at
   * £95,500.00 can be told £95,200 "exceeds available balance" — accurate, but
   * indistinguishable from a bug unless the missing £500 is named. When fees are
   * accrued, append the shared fee-gap sentence; when they are not, the gap does
   * not exist and the original generic copy is the whole message.
   *
   * The figure quoted is `feesPayableBalance` itself — NOT the shortfall on the
   * requested amount. Those coincide at exactly one input value and diverge
   * everywhere else, and it is the fee that the client can actually act on.
   */
  const capMessage = useMemo(() => {
    const generic = `Amount exceeds available balance of ${formatCurrency(netSpendCap)}`;

    return hasFeeGap(feesPayableBalance)
      ? `${generic}. ${formatInsufficientFundsMessage(feesPayableBalance)}`
      : generic;
  }, [netSpendCap, feesPayableBalance]);

  // Live over-cap state, derived from what is CURRENTLY typed — the same shape as the purchase
  // page's `exceedsBalance`. It drives both the inline warning and the disabled submit, so the
  // client is told before they commit rather than after a rejected click. A blank or zero input is
  // not "over cap": it is simply incomplete, and warning about it would be noise as they type.
  const exceedsBalance = useMemo(() => {
    const amount = parseFloat(customAmount);
    return Number.isFinite(amount) && amount > 0 && amount > netSpendCap;
  }, [customAmount, netSpendCap]);

  // The warning shown under the input. The live cap message takes precedence while the amount is
  // over cap; `submitError` still carries everything else (a failed request, a missing account, and
  // the server's 402 in the race case where the cap passed but the server disagreed).
  const inlineError = exceedsBalance ? capMessage : submitError;

  // Handle amount change from MoneyInput (decimal validation handled by MoneyInput)
  const handleAmountChange = (val: string) => {
    setCustomAmount(val);

    // Clear error if amount becomes valid. Threshold is the NET cap — clearing at
    // the displayed gross figure would drop the error for an amount that submit
    // will still refuse.
    if (submitError) {
      const amount = parseFloat(val);

      if (amount > 0 && amount <= netSpendCap) {
        setSubmitError(null);
      }
    }
  };

  // Handle submit withdrawal with fade-to-success animation
  const handleSubmitWithdrawal = async () => {
    // Validate withdrawal account is loaded
    if (!withdrawalAccount || !businessProfile?.id) {
      setSubmitError('Unable to process withdrawal. Please try again.');
      return;
    }

    // Validate amount against the NET spendable balance, never the displayed
    // gross. The message quotes the net figure too — quoting gross would tell the
    // client an amount is available that this very guard just refused.
    const amount = parseFloat(customAmount);

    // Single validation: amount exceeds the net spend cap
    // (also catches invalid/zero amounts since the cap is >= 0)
    if (!amount || amount <= 0 || amount > netSpendCap) {
      setSubmitError(capMessage);
      return;
    }

    // Clear any previous errors
    setSubmitError(null);
    setIsSubmitting(true);

    // Mint a stable idempotency key for this submission, reusing the in-flight
    // key on any concurrent re-invocation (double-click).
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    const idempotencyKey = idempotencyKeyRef.current;

    try {
      // Submit withdrawal request
      const success = await accountService.submitWithdrawalRequest(
        {
          corporateClientId: businessProfile.id,
          withdrawalBankAccountId: withdrawalAccount.id,
          amount: amount,
        },
        idempotencyKey,
      );

      if (success) {
        // Refresh business profile to update balance across the app
        // This also automatically refreshes dashboard data
        await refreshBusinessData();

        // Stage 1: Fade out the form (0.1s)
        setIsContentFading(true);

        // Stage 2: After fade-out, show success screen (starts at 0.1s)
        setTimeout(() => {
          setShowSuccessScreen(true);
          setIsSubmitting(false);
          setIsContentFading(false);
        }, 100);
      }
    } catch (error) {
      setIsSubmitting(false);
      // Allow the user to retry with a fresh key after a failure (the server
      // surfaces 409 as the static "Request is already in progress" message).
      idempotencyKeyRef.current = null;
      // #161 — the server-402 fallback trigger. The cap above normally fires
      // first, so this path is reached only when the accrued-fee figure moved
      // between the business-profile fetch and this submit (race/stale). The
      // service's relaxed 402 branch has already parsed the fee figure into the
      // thrown Error's message, so rendering `.message` is what puts
      // "£X of your balance is unavailable due to outstanding fees" into
      // MoneyInput's role="alert" slot. Every other status still arrives as its
      // D4 fixed string — never a raw server body.
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Failed to submit withdrawal request. Please try again.',
      );
      console.error('Withdrawal submission error:', error);
    }
  };

  return (
    <div className={`${styles.container} ${isContentFading ? styles.fadeOut : ''}`}>
      {' '}
      {showSuccessScreen ? (
        <SuccessScreen
          title="Withdrawal request received"
          message="Your withdrawal request has been received. A confirmation email will be sent to you when this withdrawal has been fully processed. Most withdrawals are instant."
          secondaryButton={{
            label: 'Back to home',
            onClick: handleHomeClick,
          }}
          primaryButton={{
            label: 'View wallet',
            onClick: handleBackClick,
          }}
        />
      ) : (
        <div className={`${styles.content} ${isContentFading ? styles.fadeOut : ''}`}>
          {/* Header */}
          <PageHeader
            className={styles.pageHeader}
            title="Withdraw funds"
            onBack={handleBackClick}
            subtitle="Please select the amount you wish to withdraw. Please check all your details are correct before submitting."
          />

          {/* Subtitle */}

          {/* Current Value Card */}
          <Card radius="md" className={styles.transferCard}>
            <h2 className={styles.cardTitle}>Transfer Details</h2>
            <div className={styles.cards}>
              {/* #160b — displays GROSS; the submit guard above caps at net. */}
              <BalanceCard
                as="section"
                amount={grossBalance}
                titleAs="h2"
                amountAs="div"
                className={styles.balance}
                titleClassName={styles.balanceHeading}
                amountClassName={styles.balanceFigure}
              />
              <div className={styles.accountDetailsContainer}>
                {accountsLoading ? (
                  <>
                    <Skeleton shimmer="reverse" square className={styles.accountNamePlaceholder} />
                    <div className={styles.detailsContainer}>
                      <Skeleton shimmer="reverse" square className={styles.sortCodePlaceholder} />
                      <span className={styles.circleIcon}></span>
                      <Skeleton
                        shimmer="reverse"
                        square
                        className={styles.accountNumberPlaceholder}
                      />
                    </div>
                  </>
                ) : accountsError || !withdrawalAccount ? (
                  <ErrorState
                    as="p"
                    messageAs="span"
                    className={styles.accountMissing}
                    message={accountsError || 'No withdrawal account available'}
                  />
                ) : (
                  <>
                    <p className={styles.accountName}>{withdrawalAccount.accountName}</p>
                    <div className={styles.detailsContainer}>
                      <p>{formatSortCode(withdrawalAccount.sortCode)}</p>
                      <span className={styles.circleIcon}></span>
                      <p>{withdrawalAccount.maskedAccountNumber}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>

          {/* Amount Selection */}
          <Card className={styles.amountPanel}>
            <h2 className={styles.cardTitle}>Select Amount</h2>

            <MoneyInput
              value={customAmount}
              onChange={handleAmountChange}
              error={inlineError}
              inputRef={amountInputRef}
              className={styles.amountContent}
            />
          </Card>

          {/* Action Buttons */}
          <ActionButtonPair
            cancelLabel="Cancel"
            onCancel={handleBackClick}
            cancelDisabled={isSubmitting}
            submitLabel="Submit withdrawal"
            onSubmit={handleSubmitWithdrawal}
            submitDisabled={
              accountsLoading || !withdrawalAccount || !!accountsError || exceedsBalance
            }
            isSubmitting={isSubmitting}
            className={styles.actionButtons}
          />
        </div>
      )}
    </div>
  );
};

export default Withdrawal;
